// Thin fetch wrapper for the Inkwell Worker API. Always uses same-origin and
// `credentials: "include"` so the session cookie is sent automatically.
//
// Errors are surfaced as `ApiError` so callers can branch on `status` (e.g.
// 401 → redirect to /login, 409 → version conflict).

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isAdmin: boolean;
}

export interface MeResponse extends User {
  expiresAt: number;
}

export interface AdminUser extends User {
  disabled: boolean;
  createdAt: number;
  updatedAt: number;
  lastLoginAt: number | null;
  fileCount: number;
  /** Sum of `size_bytes` of every file the user owns, including trashed. */
  storageBytes: number;
}

export type InviteStatus = "pending" | "used" | "revoked" | "expired";

export interface Invite {
  token: string;
  status: InviteStatus;
  createdBy: string;
  createdByEmail?: string;
  createdAt: number;
  expiresAt: number | null;
  usedByUserId: string | null;
  usedByEmail?: string | null;
  usedAt: number | null;
  revokedAt: number | null;
  /** Free-form admin note attached at creation time. */
  note: string | null;
}

export type FileKind = "excalidraw" | "drawio" | "notes" | "static-site";

export interface FileMeta {
  id: string;
  /** `null` when the file lives at the root level (no parent folder). */
  folderId: string | null;
  name: string;
  kind: FileKind;
  tags: string[];
  version: number;
  sizeBytes: number;
  hasThumb: boolean;
  /** Cache-bust token for `/api/files/:id/thumb`. Bumped to `now()` on
   *  every successful thumb upload; `0` means no thumb yet. */
  thumbUpdatedAt: number;
  /** Number of currently-active share tokens whose target is this file.
   *  Drives the "shared" pill on `FileCard`. Always 0 in visitor
   *  responses (folder-share listing) so recipients can't infer how
   *  many other shares the owner has. */
  activeShareCount: number;
  /** unix-ms when the owner starred this item, `null` if not starred. */
  starredAt: number | null;
  createdAt: number;
  updatedAt: number;
}

/** Compact preview info for a single file inside a folder. Returned
 *  inside `FolderMeta.previews` so `FolderCard` can render thumbnails
 *  between the folds without an extra round trip. */
export interface FilePreview {
  id: string;
  kind: FileKind;
  hasThumb: boolean;
  thumbUpdatedAt: number;
}

export interface FolderMeta {
  id: string;
  parentId: string | null;
  name: string;
  tags: string[];
  fileCount: number;
  subfolderCount: number;
  /** Up to 3 most-recently-updated files inside this folder, newest first.
   *  `previews[0]` is the front-most paper in the FolderCard stack. */
  previews: FilePreview[];
  /** Number of currently-active share tokens whose target is this folder.
   *  Drives the "shared" pill on `FolderCard`. Always 0 in visitor
   *  responses so recipients can't infer how many other shares the
   *  owner has. */
  activeShareCount: number;
  /** unix-ms when the owner starred this item, `null` if not starred. */
  starredAt: number | null;
  createdAt: number;
  updatedAt: number;
}

/** Polymorphic reference used by bulk item endpoints and the client
 *  selection model. */
export type ItemType = "file" | "folder";
export interface ItemRef {
  type: ItemType;
  id: string;
}

/** A top-level row in the Trash listing. Descendants of a trashed
 *  folder are not listed separately (they travel with the folder). */
export interface TrashItem {
  type: ItemType;
  id: string;
  name: string;
  /** Only for files. */
  kind?: FileKind;
  /** Human-readable original location, e.g. "Home / Architecture". */
  originalPath: string;
  /** Original parent folder id (`null` = root). */
  originalParentId: string | null;
  deletedAt: number;
  /** When the daily purge will permanently delete this item. */
  purgeAt: number;
  /** Folders only: number of files + folders trashed with it. */
  itemCount?: number;
  sizeBytes?: number;
}

export interface MovePrevious {
  type: ItemType;
  id: string;
  /** Parent folder before the move (`null` = root). */
  parentId: string | null;
}

export interface RestoredItem {
  type: ItemType;
  id: string;
  parentId: string | null;
  /** True when the item was restored to the root instead of its original
   *  parent: the parent is gone/trashed, or restoring there would exceed
   *  the maximum folder depth. */
  relocatedToRoot: boolean;
}

/** Max refs accepted by any `/api/items/*` endpoint (413 above). */
export const MAX_BULK_ITEMS = 500;

export interface Tag {
  id: string;
  name: string;
  fileCount: number;
  folderCount: number;
}

export interface ExcalidrawFileBlob {
  /** Optional for backward compatibility with existing stored blobs. */
  kind?: "excalidraw";
  elements: unknown[];
  appState?: Record<string, unknown>;
  files?: Record<string, unknown>;
}

export interface DrawioFileBlob {
  kind: "drawio";
  xml: string;
}

/** Canonical Markdown document. The internal `notes` kind is retained
 * for database and API compatibility. */
export interface MarkdownNotesFileBlob {
  kind: "notes";
  format: "markdown-v1";
  source: string;
}

/** Legacy BlockNote JSON, retained for lazy client-side conversion. */
export interface LegacyNotesFileBlob {
  kind: "notes";
  blocks: unknown[];
}

export type NotesFileBlob = MarkdownNotesFileBlob | LegacyNotesFileBlob;

/** One asset inside a static-site bundle. Paths are forward-slash,
 *  no leading slash, no `..`, no empty segments. */
export interface StaticSiteAsset {
  path: string;
  size: number;
  contentType: string;
  /** unix-ms of the most recent upload of this asset. */
  updatedAt: number;
}

/** Manifest for a `kind === "static-site"` file. Mutations go through
 *  the dedicated `/api/files/:id/assets*` endpoints — direct PUT of
 *  this manifest is rejected by the worker. */
export interface StaticSiteFileBlob {
  kind: "static-site";
  /** Asset path served for the root request. Must exist in `assets`. */
  entry: string;
  /** Sorted alphabetically by path. */
  assets: StaticSiteAsset[];
}

export type FileBlob = ExcalidrawFileBlob | DrawioFileBlob | NotesFileBlob | StaticSiteFileBlob;

export interface LoadedFile {
  meta: {
    id: string;
    name: string;
    kind: FileKind;
    version: number;
    updatedAt: number;
    /** Parent folder, or `null` when the file lives at the root.
     *  Only populated for owner-loaded files; share-token loads omit it. */
    folderId: string | null;
    /** Whether the server already has a thumbnail for this file. The
     *  drawio editor uses this to backfill a one-shot thumb on open
     *  for files that don't have one yet. */
    hasThumb: boolean;
    /** Owner loads only (`x-file-starred-at`); always `null` for share
     *  visitors because stars are private. */
    starredAt: number | null;
  };
  blob: FileBlob;
  /** Permission when loaded via a share token. Owner-loaded files are 'write'. */
  permission: "read" | "write";
  /** True if the share token allows downloading the file. */
  allowDownload: boolean;
  /** Share-token loads: the owner's display name (never email). `null`
   *  for owner loads or when the owner has no name set. */
  sharedBy: SharedBy | null;
  /** Share-token loads: link expiry (`x-share-expires-at`), `null` = never
   *  or owner load. */
  shareExpiresAt: number | null;
}

export interface SharedBy {
  firstName: string;
  lastName: string;
}

export type SharePermission = "read" | "write";
export type ShareTargetType = "file" | "folder";

export interface Share {
  token: string;
  targetType: ShareTargetType;
  targetId: string;
  targetName?: string;
  permission: SharePermission;
  allowDownload: boolean;
  label: string | null;
  createdAt: number;
  expiresAt: number | null;
  lastAccessedAt: number | null;
}

export interface FolderSharePayload {
  share: {
    token: string;
    permission: SharePermission;
    allowDownload: boolean;
    label: string | null;
    sharedBy: SharedBy | null;
    /** unix-ms when the link stops working; `null` = never. */
    expiresAt: number | null;
  };
  root: FolderMeta;
  folders: FolderMeta[];
  files: FileMeta[];
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public payload?: unknown,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const resp = await fetch(path, { credentials: "include", ...init });
  if (!resp.ok) {
    let payload: unknown;
    try {
      payload = await resp.json();
    } catch {
      /* non-JSON */
    }
    let msg = `HTTP ${resp.status}`;
    if (payload && typeof payload === "object" && "error" in payload) {
      msg = String((payload as { error: unknown }).error);
    }
    throw new ApiError(resp.status, msg, payload);
  }
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

function postJson<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body ?? {}),
  });
}

function patchJson<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body ?? {}),
  });
}

function putJson<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body ?? {}),
  });
}

// ─── Auth ─────────────────────────────────────────────────────────────
export const auth = {
  login: (email: string, password: string) =>
    postJson<User>("/api/auth/login", { email, password }),
  logout: () => request<void>("/api/auth/logout", { method: "POST" }),
  me: () => request<MeResponse>("/api/me"),
  changePassword: (currentPassword: string, newPassword: string) =>
    postJson<{ ok: true }>("/api/me/password", { currentPassword, newPassword }),
};

// ─── Invites (public side) ────────────────────────────────────────────
export const invites = {
  /** Validates an invite token before showing the signup form. */
  peek: (token: string) => request<{ ok: true; expiresAt: number | null }>(`/api/invites/${token}`),
  /** Accepts an invite and creates the user. Sets the session cookie on success. */
  accept: (
    token: string,
    body: { email: string; password: string; firstName: string; lastName: string },
  ) => postJson<User>(`/api/invites/${token}/accept`, body),
};

// ─── Admin ────────────────────────────────────────────────────────────
export const admin = {
  listUsers: () => request<{ users: AdminUser[] }>("/api/admin/users").then((r) => r.users),
  updateUser: (
    id: string,
    patch: Partial<{ isAdmin: boolean; disabled: boolean; firstName: string; lastName: string }>,
  ) => patchJson<AdminUser>(`/api/admin/users/${id}`, patch),
  deleteUser: (id: string) => request<{ ok: true }>(`/api/admin/users/${id}`, { method: "DELETE" }),

  listInvites: () => request<{ invites: Invite[] }>("/api/admin/invites").then((r) => r.invites),
  createInvite: (expiresInHours: number | null, note?: string | null) =>
    postJson<{
      token: string;
      url: string;
      expiresAt: number | null;
      createdAt: number;
      note: string | null;
    }>("/api/admin/invites", { expiresInHours, note: note ?? null }),
  revokeInvite: (token: string) =>
    request<{ ok: true }>(`/api/admin/invites/${token}`, { method: "DELETE" }),
};

// ─── Folders ──────────────────────────────────────────────────────────
export const folders = {
  list: () => request<{ folders: FolderMeta[] }>("/api/folders").then((r) => r.folders),
  create: (body: { name: string; parentId?: string | null; tags?: string[] }) =>
    postJson<FolderMeta>("/api/folders", body),
  update: (id: string, body: { name?: string; parentId?: string | null; tags?: string[] }) =>
    patchJson<FolderMeta>(`/api/folders/${id}`, body),
  delete: (id: string) => request<{ ok: true }>(`/api/folders/${id}`, { method: "DELETE" }),

  listShares: (id: string) =>
    request<{ tokens: Share[] }>(`/api/folders/${id}/shares`).then((r) => r.tokens),
  createShare: (
    id: string,
    body: {
      permission: SharePermission;
      allowDownload?: boolean;
      expiresAt?: number | null;
      label?: string | null;
    },
  ) => postJson<Share>(`/api/folders/${id}/shares`, body),
  revokeShare: (id: string, token: string) =>
    request<{ ok: true }>(`/api/folders/${id}/shares/${token}`, { method: "DELETE" }),
};

// ─── Tags ─────────────────────────────────────────────────────────────
export const tags = {
  list: () => request<{ tags: Tag[] }>("/api/tags").then((r) => r.tags),
  rename: (id: string, name: string) =>
    patchJson<{ id: string; name: string }>(`/api/tags/${id}`, { name }),
  delete: (id: string) => request<{ ok: true }>(`/api/tags/${id}`, { method: "DELETE" }),
};

// ─── File listing query ──────────────────────────────────────────────
export interface FilesQuery {
  /**
   * Folder filter. A real folder id scopes the listing to that folder's
   * direct children (combine with `recursive` for the whole subtree).
   * The literal string `"root"` lists files that live at the top level
   * (no parent folder). Omitting the field returns every file the
   * caller owns — used by the Recent and Search views.
   */
  folderId?: string | "root";
  recursive?: boolean;
  tags?: string[];
  q?: string;
  /** Only starred files (ordered by `starredAt DESC`). */
  starred?: boolean;
  /** Max rows (server default and cap: 1000). */
  limit?: number;
}

function buildFilesUrl(q: FilesQuery): string {
  const url = new URL("/api/files", location.origin);
  if (q.folderId) url.searchParams.set("folderId", q.folderId);
  if (q.recursive) url.searchParams.set("recursive", "1");
  for (const t of q.tags || []) url.searchParams.append("tag", t);
  if (q.q) url.searchParams.set("q", q.q);
  if (q.starred) url.searchParams.set("starred", "1");
  if (q.limit) url.searchParams.set("limit", String(q.limit));
  return url.pathname + (url.search || "");
}

// The import body is the `.excalidraw` file itself, so the name and
// destination folder travel as query params.
function buildImportUrl(opts: { name: string; folderId?: string | null }): string {
  const url = new URL("/api/files/import", location.origin);
  url.searchParams.set("name", opts.name);
  if (opts.folderId) url.searchParams.set("folderId", opts.folderId);
  return url.pathname + url.search;
}

// ─── Files ────────────────────────────────────────────────────────────
export const files = {
  list: (query: FilesQuery = {}) =>
    request<{ files: FileMeta[] }>(buildFilesUrl(query)).then((r) => r.files),
  create: (
    body: { name?: string; folderId?: string | null; tags?: string[]; kind?: FileKind } = {},
  ) => postJson<FileMeta>("/api/files", body),

  /** Creates an `excalidraw` file whose initial blob is the uploaded
   *  scene. `file` is the picked `.excalidraw` file sent verbatim — the
   *  worker parses and normalizes it, so the client never has to
   *  reproduce the stored blob shape. */
  importExcalidraw: (file: Blob, opts: { name: string; folderId?: string | null }) =>
    request<FileMeta>(buildImportUrl(opts), {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: file,
    }),
  rename: (id: string, name: string) => patchJson<FileMeta>(`/api/files/${id}`, { name }),
  /** Move a file. `folderId === null` moves to the root level. */
  move: (id: string, folderId: string | null) =>
    patchJson<FileMeta>(`/api/files/${id}`, { folderId }),
  setTags: (id: string, tagList: string[]) =>
    putJson<{ id: string; tags: string[]; updatedAt: number }>(`/api/files/${id}/tags`, {
      tags: tagList,
    }),
  delete: (id: string) => request<{ ok: true }>(`/api/files/${id}`, { method: "DELETE" }),

  /** Loads a file the current user owns. */
  async load(id: string): Promise<LoadedFile> {
    const resp = await fetch(`/api/files/${id}`, { credentials: "include" });
    if (!resp.ok) throw new ApiError(resp.status, `HTTP ${resp.status}`);
    return readFileResponse(resp, "write", true);
  },

  /** Saves a file. Throws ApiError(409) on version conflict. */
  async save(id: string, version: number, blob: FileBlob): Promise<FileMeta> {
    const resp = await fetch(`/api/files/${id}`, {
      method: "PUT",
      credentials: "include",
      headers: {
        "content-type": "application/json",
        "if-match": `"${version}"`,
      },
      body: JSON.stringify(blob),
    });
    if (!resp.ok) {
      let payload: { error?: string } | null = null;
      try {
        payload = await resp.json();
      } catch {
        /* */
      }
      throw new ApiError(resp.status, payload?.error || `HTTP ${resp.status}`, payload);
    }
    return (await resp.json()) as FileMeta;
  },

  putThumb: (id: string, svg: string) =>
    fetch(`/api/files/${id}/thumb`, {
      method: "PUT",
      credentials: "include",
      headers: { "content-type": "image/svg+xml" },
      body: svg,
    }).then((r) => {
      if (!r.ok) throw new ApiError(r.status, `HTTP ${r.status}`);
    }),

  /** Build a thumb URL with a content-addressed cache-bust token.
   *  Pass `thumbUpdatedAt` from `FileMeta` (or `FilePreview`); a
   *  zero/missing token still produces a valid URL but won't change
   *  when content changes — callers should always pass the real value. */
  thumbUrl: (id: string, bust?: number) => `/api/files/${id}/thumb${bust ? `?v=${bust}` : ""}`,

  /** Same-origin download URL that triggers a `Content-Disposition: attachment`. */
  downloadUrl: (id: string) => `/api/files/${id}/download`,

  /** Owner-side file shares. */
  listShares: (id: string) =>
    request<{ tokens: Share[] }>(`/api/files/${id}/shares`).then((r) => r.tokens),
  createShare: (
    id: string,
    body: {
      permission: SharePermission;
      allowDownload?: boolean;
      expiresAt?: number | null;
      label?: string | null;
    },
  ) => postJson<Share>(`/api/files/${id}/shares`, body),
  revokeShare: (id: string, token: string) =>
    request<{ ok: true }>(`/api/files/${id}/shares/${token}`, { method: "DELETE" }),
};

// Static-site mutators for `kind === "static-site"` files. The worker
// rejects raw `PUT /api/files/:id` for this kind — use these endpoints
// so the manifest and the R2 objects stay in lockstep. All writes
// return `{ meta, manifest }` so callers can patch both the file-list
// cache and the editor tree without an extra round trip.

export interface StaticSiteRenderSession {
  /** Absolute URL prefix (with trailing slash) for the signed render
   *  route. Concat `manifest.entry` to get the iframe `src`. */
  prefix: string;
  /** unix-ms when the signature expires. */
  expiresAt: number;
}

export interface StaticSiteMutationResult {
  meta: FileMeta;
  manifest: StaticSiteFileBlob;
}

export const staticSites = {
  /** Convenience read of the manifest. Same JSON as `files.load` but
   *  skips the binary-response header dance. */
  manifest: (id: string) => request<StaticSiteFileBlob>(`/api/files/${id}/manifest`),

  /** Upload one or more files. Bare `File` keeps the file's own name
   *  as the asset path; `{path, file}` lets the caller override
   *  (e.g. preserving `webkitRelativePath` for dropped folders).
   *  Pass `version` to enable optimistic-concurrency. */
  async uploadAssets(
    id: string,
    files: Array<File | { path: string; file: File }>,
    version?: number,
  ): Promise<StaticSiteMutationResult> {
    const fd = new FormData();
    for (const entry of files) {
      if (entry instanceof File) {
        fd.append("file", entry, entry.name);
      } else {
        fd.append("file", entry.file, entry.path);
      }
    }
    const headers: Record<string, string> = {};
    if (typeof version === "number") headers["if-match"] = `"${version}"`;
    const resp = await fetch(`/api/files/${id}/assets`, {
      method: "POST",
      credentials: "include",
      headers,
      body: fd,
    });
    return await unwrapStaticSiteResp(resp);
  },

  /** Replace the entire site with the contents of a ZIP. */
  async uploadZip(id: string, zip: Blob, version?: number): Promise<StaticSiteMutationResult> {
    const headers: Record<string, string> = { "content-type": "application/zip" };
    if (typeof version === "number") headers["if-match"] = `"${version}"`;
    const resp = await fetch(`/api/files/${id}/assets/zip`, {
      method: "POST",
      credentials: "include",
      headers,
      body: zip,
    });
    return await unwrapStaticSiteResp(resp);
  },

  async deleteAsset(id: string, path: string, version?: number): Promise<StaticSiteMutationResult> {
    const headers: Record<string, string> = {};
    if (typeof version === "number") headers["if-match"] = `"${version}"`;
    const resp = await fetch(
      `/api/files/${id}/assets/${path.split("/").map(encodeURIComponent).join("/")}`,
      { method: "DELETE", credentials: "include", headers },
    );
    return await unwrapStaticSiteResp(resp);
  },

  async setEntry(id: string, path: string, version?: number): Promise<StaticSiteMutationResult> {
    const headers: Record<string, string> = { "content-type": "application/json" };
    if (typeof version === "number") headers["if-match"] = `"${version}"`;
    const resp = await fetch(`/api/files/${id}/entry`, {
      method: "PUT",
      credentials: "include",
      headers,
      body: JSON.stringify({ path }),
    });
    return await unwrapStaticSiteResp(resp);
  },

  /** Mint a render session for the owner's preview iframe. */
  renderSession: (id: string) =>
    postJson<StaticSiteRenderSession>(`/api/files/${id}/render-session`, {}),
};

async function unwrapStaticSiteResp(resp: Response): Promise<StaticSiteMutationResult> {
  if (!resp.ok) {
    let payload: { error?: string } | null = null;
    try {
      payload = await resp.json();
    } catch {
      /* */
    }
    throw new ApiError(resp.status, payload?.error || `HTTP ${resp.status}`, payload);
  }
  return (await resp.json()) as StaticSiteMutationResult;
}

// ─── Shares (cross-target) ────────────────────────────────────────────
export const shares = {
  /** All of caller's active shares (files + folders), with target name. */
  listAll: () => request<{ shares: Share[] }>("/api/shares").then((r) => r.shares),
  revoke: (token: string) => request<{ ok: true }>(`/api/shares/${token}`, { method: "DELETE" }),

  /** Edit an existing share in place. The URL stays valid; only metadata
   *  changes. Use {@link rotate} when the URL itself must be invalidated. */
  update: (
    token: string,
    body: {
      permission?: SharePermission;
      allowDownload?: boolean;
      expiresAt?: number | null;
      label?: string | null;
    },
  ) => patchJson<Share>(`/api/shares/${token}`, body),

  /** Revoke this token and issue a fresh one with the same settings. The
   *  old URL stops working immediately. Use for leaked-link recovery. */
  rotate: (token: string) =>
    postJson<{ old: { token: string }; new: Share }>(`/api/shares/${token}/rotate`, {}),

  // ── Public token operations (file shares) ─────────────────────────
  async load(token: string): Promise<LoadedFile> {
    const resp = await fetch(`/api/share/${token}`, { credentials: "include" });
    if (!resp.ok) throw new ApiError(resp.status, `HTTP ${resp.status}`);
    const perm = (resp.headers.get("x-share-permission") as "read" | "write") || "read";
    const allowDownload = resp.headers.get("x-share-allow-download") === "1";
    return readFileResponse(resp, perm, allowDownload);
  },

  async save(token: string, version: number, blob: FileBlob): Promise<FileMeta> {
    const resp = await fetch(`/api/share/${token}`, {
      method: "PUT",
      credentials: "include",
      headers: { "content-type": "application/json", "if-match": `"${version}"` },
      body: JSON.stringify(blob),
    });
    if (!resp.ok) {
      let payload: { error?: string } | null = null;
      try {
        payload = await resp.json();
      } catch {
        /* */
      }
      throw new ApiError(resp.status, payload?.error || `HTTP ${resp.status}`, payload);
    }
    return (await resp.json()) as FileMeta;
  },

  downloadUrl: (token: string) => `/api/share/${token}/download`,

  /**
   * Resolves a token without committing to file/folder semantics. The
   * `targetType` header from the worker tells us which page to render.
   */
  async peek(
    token: string,
  ): Promise<{ type: "file"; file: LoadedFile } | { type: "folder"; payload: FolderSharePayload }> {
    const resp = await fetch(`/api/share/${token}`, { credentials: "include" });
    if (!resp.ok) throw new ApiError(resp.status, `HTTP ${resp.status}`);
    const targetType = resp.headers.get("x-share-target-type") || "file";
    if (targetType === "folder") {
      const payload = (await resp.json()) as FolderSharePayload;
      return { type: "folder", payload };
    }
    const perm = (resp.headers.get("x-share-permission") as "read" | "write") || "read";
    const allowDownload = resp.headers.get("x-share-allow-download") === "1";
    const file = await readFileResponse(resp, perm, allowDownload);
    return { type: "file", file };
  },

  // ── Public token operations (folder shares) ───────────────────────
  loadFolder: (token: string) => request<FolderSharePayload>(`/api/share/${token}`),

  async loadFolderFile(token: string, fileId: string): Promise<LoadedFile> {
    const resp = await fetch(`/api/share/${token}/files/${fileId}`, {
      credentials: "include",
    });
    if (!resp.ok) throw new ApiError(resp.status, `HTTP ${resp.status}`);
    const perm = (resp.headers.get("x-share-permission") as "read" | "write") || "read";
    const allowDownload = resp.headers.get("x-share-allow-download") === "1";
    return readFileResponse(resp, perm, allowDownload);
  },

  async saveFolderFile(
    token: string,
    fileId: string,
    version: number,
    blob: FileBlob,
  ): Promise<FileMeta> {
    const resp = await fetch(`/api/share/${token}/files/${fileId}`, {
      method: "PUT",
      credentials: "include",
      headers: { "content-type": "application/json", "if-match": `"${version}"` },
      body: JSON.stringify(blob),
    });
    if (!resp.ok) {
      let payload: { error?: string } | null = null;
      try {
        payload = await resp.json();
      } catch {
        /* */
      }
      throw new ApiError(resp.status, payload?.error || `HTTP ${resp.status}`, payload);
    }
    return (await resp.json()) as FileMeta;
  },

  folderFileThumbUrl: (token: string, fileId: string) =>
    `/api/share/${token}/files/${fileId}/thumb`,
  folderFileDownloadUrl: (token: string, fileId: string) =>
    `/api/share/${token}/files/${fileId}/download`,
  fileThumbUrl: (token: string) => `/api/share/${token}/thumb`,

  /** Mint a short-lived render session for a static-site file share. */
  renderSession: (token: string) =>
    postJson<StaticSiteRenderSession>(`/api/share/${token}/render-session`, {}),

  /** Mint a short-lived render session for a static-site file inside a
   *  folder share. The token must point at the parent folder share;
   *  `fileId` must be in the folder subtree. */
  renderSessionForFile: (token: string, fileId: string) =>
    postJson<StaticSiteRenderSession>(`/api/share/${token}/files/${fileId}/render-session`, {}),
};

// ─── Internal helpers ─────────────────────────────────────────────────
async function readFileResponse(
  resp: Response,
  permission: "read" | "write",
  allowDownload: boolean,
): Promise<LoadedFile> {
  const id = resp.headers.get("x-file-id") || "";
  const name = decodeURIComponent(resp.headers.get("x-file-name") || "Untitled");
  const kind = ((resp.headers.get("x-file-kind") || "excalidraw") as FileKind) || "excalidraw";
  const version = Number(resp.headers.get("x-file-version") || "1");
  const updatedAt = Number(resp.headers.get("x-file-updated-at") || "0");
  const folderHeader = resp.headers.get("x-file-folder-id");
  const folderId = folderHeader ? folderHeader : null;
  const hasThumb = resp.headers.get("x-file-has-thumb") === "1";
  const starredHeader = resp.headers.get("x-file-starred-at");
  const starredAt = starredHeader ? Number(starredHeader) : null;
  const sharedBy = parseSharedBy(resp.headers.get("x-share-shared-by"));
  const expHeader = resp.headers.get("x-share-expires-at");
  const shareExpiresAt = expHeader ? Number(expHeader) : null;
  const blob = (await resp.json()) as FileBlob;
  return {
    meta: { id, name, kind, version, updatedAt, folderId, hasThumb, starredAt },
    blob,
    permission,
    allowDownload,
    sharedBy,
    shareExpiresAt,
  };
}

/** `x-share-shared-by` is URI-encoded JSON (names may be non-ASCII). */
function parseSharedBy(header: string | null): SharedBy | null {
  if (!header) return null;
  try {
    const v = JSON.parse(decodeURIComponent(header)) as Partial<SharedBy> | null;
    if (!v || (typeof v.firstName !== "string" && typeof v.lastName !== "string")) return null;
    return { firstName: v.firstName ?? "", lastName: v.lastName ?? "" };
  } catch {
    return null;
  }
}

// ─── Bulk item operations (files + folders) ──────────────────────────
// All endpoints accept at most `MAX_BULK_ITEMS` refs and run as one D1
// batch. Folder operations apply to the whole subtree.
export const items = {
  /** Move into `targetFolderId` (`null` = root). 409 on a folder cycle. */
  move: (refs: ItemRef[], targetFolderId: string | null) =>
    postJson<{ previous: MovePrevious[] }>("/api/items/move", { items: refs, targetFolderId }),
  /** Soft delete (moves to Trash). */
  trash: (refs: ItemRef[]) => postJson<{ trashed: ItemRef[] }>("/api/items/trash", { items: refs }),
  restore: (refs: ItemRef[]) =>
    postJson<{ restored: RestoredItem[] }>("/api/items/restore", { items: refs }),
  /** Permanently delete already-trashed items. */
  purge: (refs: ItemRef[]) => postJson<{ ok: true }>("/api/items/purge", { items: refs }),
  star: (refs: ItemRef[], starred: boolean) =>
    postJson<{ ok: true }>("/api/items/star", { items: refs, starred }),
  /** Copies (recursively for folders) into `targetFolderId`, or next
   *  to the original when omitted. Names get a " (copy)" suffix. */
  duplicate: (refs: ItemRef[], targetFolderId?: string | null) =>
    postJson<{ files: FileMeta[]; folders: FolderMeta[] }>("/api/items/duplicate", {
      items: refs,
      ...(targetFolderId !== undefined ? { targetFolderId } : {}),
    }),
};

// ─── Trash ────────────────────────────────────────────────────────────
export const trash = {
  list: () => request<{ items: TrashItem[] }>("/api/trash").then((r) => r.items),
  /** Permanently deletes everything in Trash. */
  empty: () => request<{ purged: number }>("/api/trash", { method: "DELETE" }),
};
